import { useMemo } from 'react';

import type { BriefResponseDto } from '@inference.engine/workspace-api-client/web';
import { Separator, Spinner } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';

import { useUserDisplay } from '@/entities/user/hooks/useUserDisplay';
import { briefActivityQueryOptions } from '@/entities/brief/api/activity-queries';
import { formatDate } from '@/shared/utils/dateUtils';

import { BriefApprovalFolders } from './BriefApprovalFolders';
import { BriefOwnerSelect } from './BriefOwnerSelect';
import { mockWorkspaceUsers } from './mockData';
import type { UserSummary } from './types';
import { useTranslation } from 'react-i18next';

export interface BriefInfoTabProps {
  brief: BriefResponseDto;
  /**
   * Platform project id — the id the `projects/:platformProjectId/*` routes
   * are scoped to. Used for the activity query; `brief.projectId` is the
   * internal id and 403s against those routes.
   */
  platformProjectId?: string | null;
  isSaving?: boolean;
}

/**
 * DOM id wired between the visible "Brief owner" label and the Select
 * trigger so `htmlFor` resolves and clicking the label focuses / opens
 * the select. Module-level so the label and the trigger reference the
 * exact same string.
 */
const OWNER_TRIGGER_ID = 'brief-info-tab-owner-trigger';

/** Same label/trigger wiring as the owner field above it. */
const APPROVAL_FOLDERS_TRIGGER_ID = 'brief-info-tab-approval-folders-trigger';

export function BriefInfoTab({ brief, platformProjectId, isSaving }: BriefInfoTabProps) {
  const { t } = useTranslation('projects');

  const { data: activityEntries, isFetching: isActivityFetching } = useQuery(
    briefActivityQueryOptions({ projectId: platformProjectId ?? '', briefId: brief.id }),
  );
  const lastUpdaterId =
    typeof brief.lastUpdatedBy === 'string' ? brief.lastUpdatedBy : brief.createdBy;
  const lastUpdaterDisplay = useUserDisplay(lastUpdaterId);
  const creatorDisplay = useUserDisplay(brief.createdBy);

  // The trigger needs a name to render. While the user lookup is in flight
  // we show an em-dash; if the lookup errors we fall back to the UUID's
  // first 8 chars so the field is never blank in production. This matches
  // the no-`<Skeleton>`-in-side-panels precedent of `BriefSaveIndicator`.
  const creatorName =
    creatorDisplay.name ?? (creatorDisplay.isLoading ? '—' : shortId(brief.createdBy));

  // Build the dropdown options. The creator (resolved name + UUID) is
  // prepended so the Select always has a row matching the current value,
  // even if the creator isn't one of the mock workspace users. If the
  // creator already exists in the fixture (matched by id — won't happen
  // until BE wiring lands but kept for correctness), we don't duplicate.
  const ownerOptions = useMemo<UserSummary[]>(() => {
    const creatorOption: UserSummary = {
      id: brief.createdBy,
      name: creatorName,
      avatarUrl: creatorDisplay.avatarUrl,
    };
    const alreadyPresent = mockWorkspaceUsers.some((u) => u.id === brief.createdBy);
    return alreadyPresent ? mockWorkspaceUsers : [creatorOption, ...mockWorkspaceUsers];
  }, [brief.createdBy, creatorName, creatorDisplay.avatarUrl]);

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="brief-info-tab">
      <h2 className="text-base font-semibold text-foreground">{brief.title}</h2>

      <Separator className="bg-border" />

      <div className="flex items-start justify-between gap-4">
        <LabeledValue label={t('projectBriefDetail.briefInfoTab.createdBy')} value={creatorName} />
        <LabeledValue
          label={t('projectBriefDetail.briefInfoTab.createdAt')}
          value={formatDate(brief.createdAt)}
        />
      </div>

      <div className="flex flex-col gap-1">
        {/* `htmlFor` resolves to the trigger via the `triggerId` prop below
            so clicking the visible "Brief owner" label focuses / opens the
            select. The id is intentionally collocated with the label call
            site (per Fabricio's review on PR #2654) — without it, Radix
            auto-generates an id the caller can't reference and the label
            association is silently broken. */}
        <label htmlFor={OWNER_TRIGGER_ID} className="text-xs text-muted-foreground">
          {t('projectBriefDetail.briefInfoTab.briefOwner')}
        </label>
        {/* TODO: swap `initialOwnerId={brief.createdBy}` for
            `brief.ownerId` once the BE adds the column. Until then, the
            owner mirrors the creator so the field shows real data. */}
        <BriefOwnerSelect
          users={ownerOptions}
          initialOwnerId={brief.createdBy}
          triggerId={OWNER_TRIGGER_ID}
        />
      </div>

      {platformProjectId && (
        <div className="flex flex-col gap-1">
          <label htmlFor={APPROVAL_FOLDERS_TRIGGER_ID} className="text-xs text-muted-foreground">
            {t('projectBriefDetail.briefInfoTab.approvalFolders')}
          </label>
          <BriefApprovalFolders
            brief={brief}
            platformProjectId={platformProjectId}
            triggerId={APPROVAL_FOLDERS_TRIGGER_ID}
          />
        </div>
      )}

      <Separator className="bg-border" />

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-medium text-foreground">
            {t('projectBriefDetail.briefInfoTab.modifiedAt')}
          </h3>
          {(isSaving || isActivityFetching) && (
            <Spinner size="sm" className="text-muted-foreground" />
          )}
        </div>
        {activityEntries && activityEntries.length > 0 ? (
          <ul className="flex flex-col">
            {activityEntries.map((entry, index) => {
              const isLast = index === activityEntries.length - 1;
              return (
                <li
                  key={entry.id}
                  className={`relative flex items-center gap-2 ${isLast ? '' : 'pb-4'}`}
                >
                  <span className="size-1.5 shrink-0 rounded-full bg-muted-foreground/50" />
                  {!isLast && (
                    <span className="absolute left-[2.5px] top-[1rem] bottom-[-0.25rem] w-px bg-border" />
                  )}
                  <span className="flex-1 text-sm text-foreground/80">
                    {formatDate(entry.createdAt)}
                  </span>
                  <span className="text-sm text-muted-foreground">{entry.actor.name}</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="flex gap-3">
            <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-muted-foreground/50" />
            <div className="flex flex-1 items-center justify-between">
              <span className="text-sm text-foreground/80">{formatDate(brief.updatedAt)}</span>
              <span className="text-sm text-muted-foreground">
                {lastUpdaterDisplay.name ?? creatorName}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A label-over-value pair used in the (Created by · Created at) row.
 * Kept inline rather than promoted to `shared/` until a second consumer
 * appears (YAGNI).
 */
function LabeledValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

/** First 8 chars of a UUID — used only as a degraded fallback when the
 * user lookup errors so the field never renders blank. */
function shortId(id: string): string {
  return id.slice(0, 8);
}
